/**
 * The pause between study games, as the page draws it.
 *
 * The countdown is held on this page's clock: the server's `startsInMs` is
 * relative, so a browser clock set minutes wrong still counts the right
 * number of seconds. Every number shown is clamped to the configured pause —
 * a server answer that is late, early or from a skewed clock can shorten the
 * wait to zero but never stretch it past `betweenGamesCountdownMs`.
 */

import type { StudyServerEvents } from '@overcooked/shared'

export interface BetweenGames {
  nextGameIndex: number
  completedGameIndex: number
  replay: boolean
  /** On this page's clock. */
  startsAt: number
  /** The configured pause, the most the countdown may ever show. */
  maxMs: number
}

type CountdownPayload = Parameters<StudyServerEvents['studyNextGameCountdown']>[0]

const clamp = (value: number, low: number, high: number): number => Math.max(low, Math.min(high, value))

export function betweenFromCountdown(payload: CountdownPayload, receivedAt: number, maxMs: number): BetweenGames {
  const max = Number.isFinite(maxMs) && maxMs > 0 ? maxMs : 0
  // Relative first; the server's absolute start only when that is unusable,
  // and then trusted no further than the clamp.
  const remaining = Number.isFinite(payload.startsInMs)
    ? payload.startsInMs
    : Number.isFinite(payload.startAt)
      ? payload.startAt - receivedAt
      : max
  return {
    nextGameIndex: payload.nextGameIndex,
    completedGameIndex: payload.completedGameIndex,
    replay: payload.replay,
    startsAt: receivedAt + clamp(remaining, 0, max),
    maxMs: max,
  }
}

/** Whole seconds to show, within `0..maxMs/1000`. */
export function betweenSecondsLeft(between: BetweenGames, now: number): number {
  return clamp(Math.ceil((between.startsAt - now) / 1000), 0, Math.ceil(between.maxMs / 1000))
}

/** The pause is over once the server says a later game is the current one. */
export function betweenAfterPhase(between: BetweenGames | null, phaseGameIndex: number | null): BetweenGames | null {
  if (!between || phaseGameIndex === null) return between
  return phaseGameIndex >= between.nextGameIndex ? null : between
}

/** The header: the game just finished while paused, the current one otherwise. */
export function headerGameIndex(gameIndex: number, games: number, between: BetweenGames | null): number {
  return Math.min(between ? between.completedGameIndex : gameIndex, games)
}
